import {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  type RefObject,
  type MouseEvent as ReactMouseEvent,
  type DragEvent as ReactDragEvent,
} from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  LinearProgress,
  IconButton,
} from '@mui/material'
import DownloadIcon from '@mui/icons-material/Download'
import CloseIcon from '@mui/icons-material/Close'
import FolderZipIcon from '@mui/icons-material/FolderZip'
import Alert from '@mui/material/Alert'
import JSZip from 'jszip'
import {useTranslation} from 'react-i18next'
import {useParams, Link as RouterLink} from 'react-router-dom'
import {useAppSelector, useAppDispatch} from '../../../common/store'
import {
  selectAllScenes,
  selectActiveSceneIndex,
  selectSceneCount,
  selectSceneNames,
  setActiveScene,
} from '../../../common/components/store/scenesSlice'
import {useEditorConfig} from '../../../common/hooks/useEditorConfig'
import {serializeScene} from '../topMenuBar/serializeConfig'
import {getViewportAspect} from '../../../common/components/viewportSizeStore'
import {getProjectName} from '../../../common/components/store/projectNameStore'
import {getProjectId} from '../../../common/components/store/projectIdStore'
import {MIN_CAROUSEL_COUNT, MAX_CAROUSEL_COUNT, carouselGapPx} from '../../../common/constants/exportPresets'
import {useAppTokens} from '../../theme'
import type {ThemeTokens} from '../../theme'
import type {RenderExportFn} from '../../../common/components/ExportHandler'
import {addBreadcrumb} from '../../../common/errors/breadcrumb'
import {handleApiError} from '../../../common/errors/handleApiError'
import {trackEvent} from '../../../common/analytics/trackEvent'
import {downloadFile, dataUrlToBlob} from '../../../common/utils/downloadFile'
import FreeExportAdPanel from './FreeExportAdPanel'

// ── Filename helpers ─────────────────────────────────────────────

// Filesystem-safe slug: strip illegal chars (\/:*?"<>|), collapse whitespace,
// trim trailing dots/spaces (Windows). Falls back to "untitled" if empty.
const sanitizeName = (raw: string): string => {
  const cleaned = raw
    .replace(/[\\/:*?"<>|]/g, '')
    .replace(/\s+/g, ' ')
    .replace(/[.\s]+$/g, '')
    .trim()
  return cleaned.length > 0 ? cleaned : 'untitled'
}

// Append "-2", "-3", … to a scene label (folder/file base) when the same scene
// name already exists in the ZIP, so same-named scenes never collide.
const uniqueLabel = (base: string, used: Set<string>): string => {
  let candidate = base
  let counter = 2
  while (used.has(candidate)) {
    candidate = `${base}-${counter}`
    counter++
  }
  used.add(candidate)
  return candidate
}

// ── Delay helper ─────────────────────────────────────────────────

const delay = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms))

// The device geometry runs in a Web Worker (CSG). `useDeviceGeometry` sets
// `window.__SCENE_READY` true once the mesh for the active scene is delivered
// (and false while it rebuilds). We poll it before each capture so the export
// never grabs a background-only frame while a heavy device is still building.
// Generous timeout so a slow build (large iPad) still completes; on timeout we
// fall through and capture whatever is there rather than hanging forever.
const GEOMETRY_READY_TIMEOUT_MS = 60000
const GEOMETRY_POLL_MS = 80

const isSceneReady = () => (window as unknown as {__SCENE_READY?: boolean}).__SCENE_READY === true

const waitForGeometryReady = async (cancelled: RefObject<boolean>) => {
  const start = performance.now()
  while (!isSceneReady()) {
    if (cancelled.current) return
    if (performance.now() - start > GEOMETRY_READY_TIMEOUT_MS) {
      addBreadcrumb('render', 'geometry-ready-timeout', 'warning')
      return
    }
    await delay(GEOMETRY_POLL_MS)
  }
}

// ── Carousel types ───────────────────────────────────────────────

interface PreviewItem {
  sceneIndex: number
  /** null = single-frame scene; 0..N-1 for carousel slices */
  carouselIndex: number | null
  /** 1 for non-carousel; N for a carousel scene */
  carouselCount: number
  url: string | null
}

const clampCarouselCount = (n: number): number =>
  Math.min(MAX_CAROUSEL_COUNT, Math.max(MIN_CAROUSEL_COUNT, Math.round(n)))

// ── Constants ────────────────────────────────────────────────────

const SCENE_SWITCH_DELAY_MS = 400
// Free-mode export holds the progress UI for a randomised window in this range
// so we have room to show the upsell panel — actual rendering finishes in well
// under a second, and randomising prevents the delay from feeling too uniform
// to returning visitors.
const FREE_MIN_DURATION_MS = 7000
const FREE_MAX_DURATION_MS = 10000
// Cap the synthetic progress here while the real render is still in flight
// (the >duration edge case); jump to 100% only when both conditions are met.
const FREE_PROGRESS_CEILING = 95
const pickFreeDuration = () => FREE_MIN_DURATION_MS + Math.random() * (FREE_MAX_DURATION_MS - FREE_MIN_DURATION_MS)

// Inline style for the Ecranify mark inside the shimmer container — kept at
// module scope so it doesn't churn on every render. Matches BrandLogo.tsx.
const shimmerMarkImgStyle = {
  width: '64%',
  height: '64%',
  objectFit: 'contain' as const,
  filter: 'brightness(0) invert(1)',
  display: 'block',
  position: 'relative' as const,
  zIndex: 1,
}
const MAGNIFIER_SIZE = 200
const MAGNIFIER_ZOOM = 7
const MAGNIFIER_OFFSET = MAGNIFIER_SIZE / 2

// ── Magnifier sub-component ──────────────────────────────────────
// Renders preview on <canvas> to prevent right-click "Save Image As"

interface MagnifierProps {
  src: string
  tokens: ThemeTokens
}

const MagnifierCanvas = ({src, tokens}: MagnifierProps) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const lensRef = useRef<HTMLDivElement>(null)

  const halfSize = MAGNIFIER_SIZE / 2

  // Draw data-URL image onto canvas whenever src changes
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas || !src) return

    const img = new Image()
    img.onload = () => {
      // Limit canvas display size while keeping aspect ratio
      const maxW = canvas.parentElement?.clientWidth ?? 600
      const maxH = 400
      const scale = Math.min(1, maxW / img.width, maxH / img.height)
      canvas.width = img.width * scale
      canvas.height = img.height * scale
      const ctx = canvas.getContext('2d')
      if (ctx) ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
    }
    img.src = src
  }, [src])

  const handleMouseMove = useCallback(
    (e: ReactMouseEvent<HTMLDivElement>) => {
      const canvas = canvasRef.current
      const lens = lensRef.current
      if (!canvas || !lens) return

      const canvasRect = canvas.getBoundingClientRect()
      const canvasX = e.clientX - canvasRect.left
      const canvasY = e.clientY - canvasRect.top

      if (canvasX < 0 || canvasX > canvasRect.width || canvasY < 0 || canvasY > canvasRect.height) {
        lens.style.display = 'none'
        e.currentTarget.style.cursor = 'default'
        return
      }

      const bgX = (canvasX / canvasRect.width) * 100
      const bgY = (canvasY / canvasRect.height) * 100

      lens.style.display = 'block'
      lens.style.left = `${e.clientX - halfSize + MAGNIFIER_OFFSET}px`
      lens.style.top = `${e.clientY - halfSize + MAGNIFIER_OFFSET}px`
      lens.style.backgroundPosition = `${bgX}% ${bgY}%`
      e.currentTarget.style.cursor = 'crosshair'
    },
    [halfSize],
  )

  const handleMouseLeave = useCallback(() => {
    const lens = lensRef.current
    if (lens) lens.style.display = 'none'
    const container = containerRef.current
    if (container) container.style.cursor = 'default'
  }, [])

  const handleContextMenu = useCallback((e: ReactMouseEvent<HTMLDivElement>) => {
    e.preventDefault()
  }, [])

  const handleDragStart = useCallback((e: ReactDragEvent<HTMLDivElement>) => {
    e.preventDefault()
  }, [])

  return (
    <Box
      ref={containerRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      onContextMenu={handleContextMenu}
      onDragStart={handleDragStart}
      sx={{
        position: 'relative',
        display: 'flex',
        justifyContent: 'center',
        width: '100%',
      }}
    >
      <canvas
        ref={canvasRef}
        draggable={false}
        style={{
          maxWidth: '100%',
          maxHeight: 400,
          objectFit: 'contain',
          borderRadius: '6px',
          border: `1px solid ${tokens.accordion.border}`,
        }}
      />
      <Box
        ref={lensRef}
        sx={{
          display: 'none',
          position: 'fixed',
          width: MAGNIFIER_SIZE,
          height: MAGNIFIER_SIZE,
          borderRadius: '50%',
          border: `2px solid ${tokens.text.muted}`,
          boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
          backgroundImage: `url(${src})`,
          backgroundSize: `${MAGNIFIER_ZOOM * 100}%`,
          backgroundRepeat: 'no-repeat',
          pointerEvents: 'none',
          zIndex: 9999,
        }}
      />
    </Box>
  )
}

// ── ExportDialog — modal with multi-scene export previews ────────

interface ExportDialogProps {
  open: boolean
  onClose: () => void
  renderExportRef: RefObject<RenderExportFn | null>
  onRenderingChange?: (rendering: boolean) => void
  freeMode?: boolean
}

const ExportDialog = ({open, onClose, renderExportRef, onRenderingChange, freeMode}: ExportDialogProps) => {
  const {t} = useTranslation()
  const tokens = useAppTokens()
  const dispatch = useAppDispatch()
  const {deviceConfig} = useEditorConfig()
  const {projectId} = useParams()

  const scenes = useAppSelector(selectAllScenes)
  const sceneNames = useAppSelector(selectSceneNames)
  const activeSceneIndex = useAppSelector(selectActiveSceneIndex)
  const sceneCount = useAppSelector(selectSceneCount)

  const [previewItems, setPreviewItems] = useState<PreviewItem[]>([])
  const [renderingIndex, setRenderingIndex] = useState<number | null>(null)
  const [creditError, setCreditError] = useState<string | null>(null)
  const [renderCost, setRenderCost] = useState<number | null>(null)
  const cancelledRef = useRef(false)

  // Total frames expected across all scenes — a non-carousel scene contributes 1,
  // a carousel scene contributes its clamped count. Drives the "done" check.
  const totalExpectedFrames = useMemo(
    () => scenes.reduce((sum, s) => sum + (s.exportCarouselEnabled ? clampCarouselCount(s.exportCarouselCount) : 1), 0),
    [scenes],
  )

  // Free-mode synthetic progress — animates 0 → 100% over a per-open random
  // window in [FREE_MIN_DURATION_MS, FREE_MAX_DURATION_MS] so the upsell panel
  // has time to be read. Paid mode ignores all four.
  const freeStartRef = useRef<number | null>(null)
  const freeDurationRef = useRef<number | null>(null)
  const [freeProgress, setFreeProgress] = useState(0)
  const [freeReady, setFreeReady] = useState(false)

  // Fetch render cost once — used to label download buttons with credit cost
  useEffect(() => {
    if (freeMode || renderCost !== null) return
    let cancelled = false
    import('../../../common/api/generated/endpoints/credits/credits')
      .then(({getPackages}) => getPackages())
      .then(({data}) => {
        if (!cancelled) setRenderCost(data.renderCost)
      })
      .catch((err) => handleApiError(err, 'export-dialog-packages'))
    return () => {
      cancelled = true
    }
  }, [freeMode, renderCost])

  // Derive rendering state — dialog open but previews not yet ready.
  // In free mode we additionally hold the UI until the synthetic 7s window
  // has elapsed so the upsell panel is actually readable.
  const realRendering = open && previewItems.length < totalExpectedFrames
  const rendering = freeMode ? open && !freeReady : realRendering

  // Reset free-mode timer state whenever the dialog opens/closes
  useEffect(() => {
    if (!open) {
      freeStartRef.current = null
      freeDurationRef.current = null
      setFreeProgress(0)
      setFreeReady(false)
    } else {
      trackEvent('export_dialog_opened', {
        editor_mode: freeMode ? 'free' : 'paid',
        scene_count: sceneCount,
      })
    }
  }, [open, freeMode, sceneCount])

  // Fire upsell_shown once per session in free mode — the upsell panel appears alongside
  // the synthetic-progress bar (rendering branch) and the mini-strip (post-render).
  const upsellFiredRef = useRef(false)
  useEffect(() => {
    if (!open) {
      upsellFiredRef.current = false
      return
    }
    if (freeMode && !upsellFiredRef.current) {
      upsellFiredRef.current = true
      trackEvent('upsell_shown', {surface: 'free_export'})
    }
  }, [open, freeMode])

  // Drive the synthetic progress bar (ease-out cubic) while in free mode loading
  useEffect(() => {
    if (!freeMode || !open || freeReady) return
    if (freeStartRef.current === null) freeStartRef.current = performance.now()
    if (freeDurationRef.current === null) freeDurationRef.current = pickFreeDuration()

    let rafId = 0
    const tick = () => {
      const start = freeStartRef.current ?? performance.now()
      const duration = freeDurationRef.current ?? FREE_MIN_DURATION_MS
      const elapsed = performance.now() - start
      const ratio = Math.min(1, elapsed / duration)
      const eased = 1 - Math.pow(1 - ratio, 3)
      const renderDone = previewItems.length >= totalExpectedFrames
      const ceiling = renderDone ? 100 : FREE_PROGRESS_CEILING
      const next = Math.min(eased * 100, ceiling)
      setFreeProgress(next)

      if (elapsed >= duration && renderDone) {
        setFreeProgress(100)
        setFreeReady(true)
        return
      }
      rafId = requestAnimationFrame(tick)
    }
    rafId = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(rafId)
  }, [freeMode, open, freeReady, previewItems.length, totalExpectedFrames])

  // Render all scenes sequentially when dialog opens
  useEffect(() => {
    if (!open) return

    cancelledRef.current = false
    const originalIndex = activeSceneIndex

    // ViewportV2 dispatches this when the WebGL context dies mid-render — on
    // mobile (iOS Safari/Chrome) the export buffer + shadow map can exhaust the
    // tab's GPU budget and the browser kills the context. Aborting here keeps
    // the dialog responsive instead of letting the page crash silently.
    const handleWebglLost = () => {
      cancelledRef.current = true
      setCreditError(t('exportDialog.webglContextLost'))
      setRenderingIndex(null)
      onRenderingChange?.(false)
      trackEvent('export_render_failed', {error_code: 'webgl_context_lost'})
    }
    window.addEventListener('app:webgl-lost', handleWebglLost)

    const renderAll = async () => {
      const renderExport = renderExportRef.current
      if (!renderExport) return

      setCreditError(null)
      onRenderingChange?.(true)
      addBreadcrumb('render', 'export-start', 'info', {scenes: scenes.length, freeMode})
      const renderStartedAt = performance.now()
      trackEvent('export_render_started', {
        editor_mode: freeMode ? 'free' : 'paid',
        scene_count: scenes.length,
        total_frames: totalExpectedFrames,
      })

      const items: PreviewItem[] = []

      for (let i = 0; i < scenes.length; i++) {
        if (cancelledRef.current) return

        setRenderingIndex(i)
        dispatch(setActiveScene(i))
        // Let React re-render and the new scene's geometry worker (re)dispatch,
        // then wait until the device mesh is actually present before capturing —
        // a fixed delay alone can capture a background-only frame when a heavy
        // device (many CSG features) hasn't finished building yet.
        await delay(SCENE_SWITCH_DELAY_MS)
        if (cancelledRef.current) return
        await waitForGeometryReady(cancelledRef)
        if (cancelledRef.current) return

        const scene = scenes[i]
        const isCarousel = scene.exportCarouselEnabled
        const N = isCarousel ? clampCarouselCount(scene.exportCarouselCount) : 1

        if (!isCarousel) {
          const canvas = await renderExport(scene.exportWidth, scene.exportHeight)
          items.push({
            sceneIndex: i,
            carouselIndex: null,
            carouselCount: 1,
            url: canvas ? canvas.toDataURL('image/png') : null,
          })
        } else {
          // Per-slice render via `camera.setViewOffset` — each slice renders at
          // its native dimensions, avoiding the WebGL MAX_TEXTURE_SIZE clip the
          // wide approach hit at carousel ≥ 3 (default 1290 × 3 × SSAA = 7740px,
          // crossed 4k/8k GPU caps on some platforms and silently truncated).
          // Empty gutter between frames (in px), matching the App Store gallery
          // spacing so the composition stays aligned. Each slice still renders at
          // its native dimensions; the gutter region is never drawn into a slice.
          const gapPx = carouselGapPx(scene.exportWidth, scene.exportCarouselGap)
          for (let k = 0; k < N; k++) {
            const slice = await renderExport(scene.exportWidth, scene.exportHeight, {
              fullWidth: scene.exportWidth * N + gapPx * (N - 1),
              fullHeight: scene.exportHeight,
              offsetX: k * (scene.exportWidth + gapPx),
            })
            items.push({
              sceneIndex: i,
              carouselIndex: k,
              carouselCount: N,
              url: slice ? slice.toDataURL('image/png') : null,
            })
            setPreviewItems([...items])
            if (cancelledRef.current) return
            // Yield to the event loop so Chromium doesn't show the
            // "page unresponsive" prompt during large (count=5) carousels.
            await delay(0)
          }
        }
        setPreviewItems([...items])
      }

      dispatch(setActiveScene(originalIndex))
      await delay(100)

      setRenderingIndex(null)
      onRenderingChange?.(false)
      trackEvent('export_render_completed', {
        editor_mode: freeMode ? 'free' : 'paid',
        item_count: items.length,
        total_time_ms: Math.round(performance.now() - renderStartedAt),
      })
    }

    const timeout = setTimeout(() => {
      renderAll()
    }, 100)

    return () => {
      clearTimeout(timeout)
      cancelledRef.current = true
      window.removeEventListener('app:webgl-lost', handleWebglLost)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const handleClose = useCallback(() => {
    cancelledRef.current = true
    setPreviewItems([])
    setRenderingIndex(null)
    setCreditError(null)
    freeStartRef.current = null
    freeDurationRef.current = null
    setFreeProgress(0)
    setFreeReady(false)
    onRenderingChange?.(false)
    onClose()
  }, [onClose, onRenderingChange])

  // Charge credits and create render history — returns true on success
  // Skipped entirely in free mode (no auth, no credits)
  const chargeFrames = useCallback(
    async (items: PreviewItem[]): Promise<boolean> => {
      if (freeMode) return true
      setCreditError(null)
      try {
        const {createRender} = await import('../../../common/api/generated/endpoints/renders/renders')
        // Editor viewport aspect at export time — the server render uses it to
        // map 2D texts/sprites into the frame exactly like this client export
        // (its own canvas aspect differs). Injected into the render-only config
        // (not serializeScene) so project saves stay clean.
        const refAspect = getViewportAspect()
        const sceneConfigs = items.map((item) => {
          const baseName = sceneNames[item.sceneIndex] ?? `Scene ${item.sceneIndex + 1}`
          const name =
            item.carouselIndex === null
              ? baseName
              : `${baseName} — frame ${item.carouselIndex + 1}/${item.carouselCount}`
          const serialized = serializeScene(scenes[item.sceneIndex], deviceConfig) as unknown as Record<string, unknown>
          const exportBlock = {...(serialized.export as Record<string, unknown> | undefined), refAspect}
          return {
            config: {...serialized, export: exportBlock} as unknown as Record<string, unknown>,
            name,
            carouselIndex: item.carouselIndex,
            carouselCount: item.carouselCount,
          }
        })
        // Prefer the live project id from the store — `useParams()` stays stale after the first
        // save because EditorTopBar swaps the URL via `history.replaceState` (no remount).
        await createRender({
          clientRenderId: crypto.randomUUID(),
          projectName: getProjectName() ?? 'Untitled',
          projectId: getProjectId() ?? projectId,
          scenes: sceneConfigs,
        })
        if (renderCost !== null) {
          trackEvent('render_credit_charged', {
            credits_charged: renderCost * items.length,
            item_count: items.length,
          })
        }
        return true
      } catch (err) {
        const status = (err as {status?: number}).status
        setCreditError(t(status === 402 ? 'exportDialog.insufficientCredits' : 'exportDialog.renderChargeError'))
        if (status === 402) {
          trackEvent('export_credit_insufficient', {item_count: items.length})
        } else {
          trackEvent('export_render_failed', {error_code: typeof status === 'number' ? String(status) : 'unknown'})
        }
        return false
      }
    },
    [t, scenes, sceneNames, deviceConfig, freeMode, projectId, renderCost],
  )

  // Download a single frame PNG (single-scene or carousel-slice)
  const handleDownloadFrame = useCallback(
    async (item: PreviewItem) => {
      if (!item.url) return
      if (!(await chargeFrames([item]))) return

      const baseName = sanitizeName(sceneNames[item.sceneIndex] ?? `Scene ${item.sceneIndex + 1}`)
      const fileName = item.carouselIndex === null ? `${baseName}.png` : `${baseName}-${item.carouselIndex + 1}.png`
      const blob = await dataUrlToBlob(item.url)
      await downloadFile(blob, fileName)
      trackEvent('export_downloaded', {
        mode: 'single',
        editor_mode: freeMode ? 'free' : 'paid',
        frame_count: 1,
      })
    },
    [sceneNames, chargeFrames, freeMode],
  )

  // Download all carousel frames of a single scene as a ZIP
  const handleDownloadCarouselGroup = useCallback(
    async (sceneIdx: number) => {
      const groupItems = previewItems.filter((it) => it.sceneIndex === sceneIdx && it.url !== null)
      if (groupItems.length === 0) return
      if (!(await chargeFrames(groupItems))) return

      const baseName = sanitizeName(sceneNames[sceneIdx] ?? `Scene ${sceneIdx + 1}`)
      const zip = new JSZip()
      for (const item of groupItems) {
        const blob = await dataUrlToBlob(item.url!)
        const idx = (item.carouselIndex ?? 0) + 1
        zip.file(`${baseName}/frame-${idx}.png`, blob)
      }
      const zipBlob = await zip.generateAsync({type: 'blob'})
      await downloadFile(zipBlob, `${baseName}-carousel.zip`)
      trackEvent('export_downloaded', {
        mode: 'group',
        editor_mode: freeMode ? 'free' : 'paid',
        frame_count: groupItems.length,
      })
    },
    [previewItems, sceneNames, chargeFrames, freeMode],
  )

  // Download every frame across every scene as one ZIP — bucket by export preset folder.
  // Organized by scene, using each scene's real name:
  //   single-frame scene → `{scene}.png`
  //   carousel scene     → `{scene}/frame-K.png`
  // Scene labels are deduped so same-named scenes never collide/overwrite.
  const handleDownloadAll = useCallback(async () => {
    const validItems = previewItems.filter((it) => it.url !== null)
    if (validItems.length === 0) return
    if (!(await chargeFrames(validItems))) return

    // Group items by scene, preserving first-seen order.
    const order: number[] = []
    const bySceneIndex = new Map<number, PreviewItem[]>()
    for (const item of validItems) {
      let group = bySceneIndex.get(item.sceneIndex)
      if (!group) {
        group = []
        bySceneIndex.set(item.sceneIndex, group)
        order.push(item.sceneIndex)
      }
      group.push(item)
    }

    const zip = new JSZip()
    const usedLabels = new Set<string>()

    for (const sceneIdx of order) {
      const group = bySceneIndex.get(sceneIdx)!
      const rawName = sanitizeName(sceneNames[sceneIdx] ?? `Scene ${sceneIdx + 1}`)
      const label = uniqueLabel(rawName, usedLabels)
      const isCarousel = group.length > 1 || group[0].carouselIndex !== null
      for (const item of group) {
        const blob = await dataUrlToBlob(item.url!)
        const path = isCarousel ? `${label}/frame-${(item.carouselIndex ?? 0) + 1}.png` : `${label}.png`
        zip.file(path, blob)
      }
    }

    const zipBlob = await zip.generateAsync({type: 'blob'})
    const projectBase = sanitizeName(getProjectName() ?? 'export')
    await downloadFile(zipBlob, `${projectBase}.zip`)
    trackEvent('export_downloaded', {
      mode: 'zip',
      editor_mode: freeMode ? 'free' : 'paid',
      frame_count: validItems.length,
    })
  }, [previewItems, sceneNames, chargeFrames, freeMode])

  const sx = useMemo(() => buildStyles(tokens), [tokens])

  // Adaptive maxWidth: xs for 1 scene, sm for 2, md for 3+. Carousels need
  // 'lg' so 3..5 frames have room to sit side-by-side without crushing each
  // other. Bump to 'md' in free-mode loading so the upsell panel fits beside.
  const hasCarousel = scenes.some((s) => s.exportCarouselEnabled)
  const dialogMaxWidth = hasCarousel
    ? 'lg'
    : freeMode && rendering
      ? 'md'
      : sceneCount >= 3
        ? 'md'
        : sceneCount === 2
          ? 'sm'
          : 'xs'

  // Credit cost suffix appended to download button labels (skipped in free mode)
  const formatCost = (count: number) =>
    !freeMode && renderCost !== null ? ` (${t('exportDialog.creditCost', {count: renderCost * count})})` : ''
  const singleCostLabel = formatCost(1)
  const allCostLabel = formatCost(totalExpectedFrames)

  // Group preview items by scene for rendering — preserves original order.
  const previewGroups = useMemo(() => {
    const map = new Map<number, PreviewItem[]>()
    for (const item of previewItems) {
      const list = map.get(item.sceneIndex) ?? []
      list.push(item)
      map.set(item.sceneIndex, list)
    }
    return Array.from(map.entries()).sort(([a], [b]) => a - b)
  }, [previewItems])

  // Footer button decision: show "Download all (ZIP)" whenever more than one frame
  // exists in total — handles multi-scene AND single-scene-carousel.
  const previewsReady = previewItems.length >= totalExpectedFrames && previewItems.every((it) => it.url !== null)
  const showMultiDownload = totalExpectedFrames > 1

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={dialogMaxWidth as 'xs' | 'sm' | 'md'}
      fullWidth
      slotProps={{paper: {sx: sx.paper}}}
    >
      <DialogTitle sx={sx.title}>
        {t('exportDialog.title')}
        <IconButton onClick={handleClose} size="small" sx={sx.closeIconButton}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={sx.content}>
        {/* Credit error */}
        {creditError && (
          <Alert severity="error" sx={{fontSize: 13}}>
            {creditError}
          </Alert>
        )}

        {/* Rendering progress */}
        {rendering && (
          <Box sx={freeMode ? sx.loadingBoxFree : sx.loadingBox}>
            <Box sx={freeMode ? sx.loadingColumnFree : sx.loadingColumn}>
              <Box sx={sx.shimmerMark} aria-hidden>
                <img src="/logo.svg" alt="" style={shimmerMarkImgStyle} />
                <Box sx={sx.shimmerSweep} />
              </Box>
              <Typography sx={sx.loadingText}>
                {t('exportDialog.renderingScene', {
                  index: (renderingIndex ?? 0) + 1,
                  total: sceneCount,
                })}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={freeMode ? freeProgress : (previewItems.length / totalExpectedFrames) * 100}
                sx={sx.progressBar}
              />
            </Box>
            {freeMode && (
              <Box sx={sx.adColumn}>
                <FreeExportAdPanel />
              </Box>
            )}
          </Box>
        )}

        {/* Mini upsell strip — free mode, after previews are ready */}
        {freeMode && !rendering && previewItems.length > 0 && (
          <Box sx={sx.miniAd}>
            <Typography sx={sx.miniAdText}>{t('freeInfoColumn.miniPrompt')}</Typography>
            <Box component={RouterLink} to="/login" sx={sx.miniAdCta}>
              {t('freeInfoColumn.cta')}
              <svg
                width={12}
                height={12}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden
              >
                <path d="M5 12h14" />
                <path d="M13 5l7 7-7 7" />
              </svg>
            </Box>
          </Box>
        )}

        {/* Scene previews — shown only after all scenes are rendered. Carousel scenes
            render as a grouped row of frame cards with a single group-level "Download
            Carousel" button. Non-carousel scenes match the prior single-card layout. */}
        {!rendering && previewItems.length > 0 && (
          <Box sx={sx.scenesGrid}>
            {previewGroups.map(([sceneIdx, group]) => {
              const scene = scenes[sceneIdx]
              const displayName = sceneNames[sceneIdx]?.trim() || t('exportDialog.sceneLabel', {index: sceneIdx + 1})
              const isCarousel = group[0]?.carouselCount > 1
              const groupCostLabel = formatCost(group.length)

              if (!isCarousel) {
                const item = group[0]
                return (
                  <Box key={sceneIdx} sx={sx.sceneCard}>
                    <Box sx={sx.sceneHeader}>
                      <Typography sx={sx.sceneLabel} title={displayName}>
                        {displayName}
                      </Typography>
                      <Typography sx={sx.dimensionLabel}>
                        {scene.exportWidth} × {scene.exportHeight}
                      </Typography>
                    </Box>
                    {item?.url && <MagnifierCanvas src={item.url} tokens={tokens} />}
                    {sceneCount > 1 && (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                        onClick={() => handleDownloadFrame(item)}
                        disabled={!item?.url}
                        sx={sx.sceneDownloadButton}
                      >
                        {t('exportDialog.download')}
                        {singleCostLabel}
                      </Button>
                    )}
                  </Box>
                )
              }

              return (
                <Box key={sceneIdx} sx={sx.carouselGroup}>
                  <Box sx={sx.sceneHeader}>
                    <Box sx={sx.carouselHeaderInfo}>
                      <Typography sx={sx.sceneLabel} title={displayName}>
                        {displayName}
                      </Typography>
                      <Typography sx={sx.dimensionLabel}>
                        {t('exportDialog.carouselLabel', {count: group.length})} · {scene.exportWidth} ×{' '}
                        {scene.exportHeight}
                      </Typography>
                    </Box>
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<FolderZipIcon />}
                      onClick={() => handleDownloadCarouselGroup(sceneIdx)}
                      disabled={group.some((it) => !it.url)}
                      sx={sx.groupDownloadButton}
                    >
                      {t('exportDialog.downloadCarousel')}
                      {groupCostLabel}
                    </Button>
                  </Box>
                  <Box sx={{...sx.carouselFrames, gridTemplateColumns: `repeat(${group.length}, minmax(0, 1fr))`}}>
                    {group.map((item) => (
                      <Box key={item.carouselIndex ?? 0} sx={sx.frameCard}>
                        <Typography sx={sx.frameLabel}>
                          {t('exportDialog.frameLabel', {
                            index: (item.carouselIndex ?? 0) + 1,
                            total: item.carouselCount,
                          })}
                        </Typography>
                        {item.url && <MagnifierCanvas src={item.url} tokens={tokens} />}
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<DownloadIcon />}
                          onClick={() => handleDownloadFrame(item)}
                          disabled={!item.url}
                          sx={sx.sceneDownloadButton}
                        >
                          {t('exportDialog.downloadFrame', {index: (item.carouselIndex ?? 0) + 1})}
                          {singleCostLabel}
                        </Button>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )
            })}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={sx.actions}>
        <Button onClick={handleClose} startIcon={<CloseIcon />} sx={sx.closeButton}>
          {t('exportDialog.close')}
        </Button>
        {showMultiDownload ? (
          <Button
            variant="contained"
            onClick={handleDownloadAll}
            disabled={rendering || !previewsReady}
            startIcon={<FolderZipIcon />}
            sx={sx.downloadButton}
          >
            {t('exportDialog.downloadAll')}
            {allCostLabel}
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={() => previewItems[0] && handleDownloadFrame(previewItems[0])}
            disabled={rendering || !previewItems[0]?.url}
            startIcon={<DownloadIcon />}
            sx={sx.downloadButton}
          >
            {t('exportDialog.download')}
            {singleCostLabel}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}

// ── Styles ────────────────────────────────────────────────────────

const buildStyles = (tokens: ThemeTokens) => ({
  paper: {
    backgroundColor: tokens.sidebar.background,
    color: tokens.text.primary,
  },
  title: {
    fontSize: 16,
    fontWeight: 600,
    color: tokens.text.primary,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  closeIconButton: {
    color: tokens.text.muted,
  },
  content: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '16px',
    minHeight: 200,
    maxHeight: '70vh',
    overflowY: 'auto' as const,
  },
  scenesGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
    gap: '12px',
    maxWidth: '100%',
  },
  miniAd: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    padding: '10px 14px',
    borderRadius: '10px',
    border: `1px solid ${tokens.accordion.border}`,
    background: 'linear-gradient(90deg, rgba(124, 92, 255, 0.08), rgba(124, 92, 255, 0.02))',
    flexWrap: {xs: 'wrap', sm: 'nowrap'} as const,
  },
  miniAdText: {
    fontSize: 12.5,
    color: tokens.text.secondary,
    lineHeight: 1.35,
    minWidth: 0,
    flex: 1,
  },
  miniAdCta: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    flexShrink: 0,
    height: 30,
    padding: '0 12px',
    borderRadius: '8px',
    background: 'linear-gradient(180deg, #8a6dff, #6446f5)',
    color: '#ffffff',
    fontSize: 12,
    fontWeight: 600,
    textDecoration: 'none',
    boxShadow: '0 6px 18px -6px rgba(124, 92, 255, 0.55), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
    transition: 'background 0.18s, transform 0.15s',
    '&:hover': {
      background: 'linear-gradient(180deg, #9379ff, #7053ff)',
      transform: 'translateY(-1px)',
    },
  },
  loadingBox: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    padding: '60px 40px',
    width: '100%',
  },
  shimmerMark: {
    position: 'relative' as const,
    width: 96,
    height: 96,
    borderRadius: '20px',
    background: `linear-gradient(135deg, ${tokens.accent.lightest} 0%, ${tokens.accent.light} 30%, ${tokens.accent.primary} 65%, ${tokens.accent.dark} 100%)`,
    boxShadow: `0 0 24px ${tokens.accent.glow}, inset 0 1px 0 rgba(255, 255, 255, 0.25)`,
    overflow: 'hidden' as const,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '4px',
    '@keyframes ecranify-shimmer': {
      '0%': {transform: 'translateX(-100%)'},
      '100%': {transform: 'translateX(100%)'},
    },
    '@keyframes ecranify-pulse': {
      '0%, 100%': {transform: 'scale(1)'},
      '50%': {transform: 'scale(1.04)'},
    },
    animation: 'ecranify-pulse 2.4s ease-in-out infinite',
  },
  shimmerSweep: {
    position: 'absolute' as const,
    inset: 0,
    background:
      'linear-gradient(110deg, transparent 0%, transparent 35%, rgba(255, 255, 255, 0.55) 50%, transparent 65%, transparent 100%)',
    animation: 'ecranify-shimmer 1.8s ease-in-out infinite',
    pointerEvents: 'none' as const,
  },
  loadingBoxFree: {
    display: 'flex',
    flexDirection: {xs: 'column', sm: 'row'} as const,
    alignItems: 'stretch',
    justifyContent: 'center',
    gap: '24px',
    padding: '32px 24px',
    width: '100%',
  },
  loadingColumn: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    width: '100%',
  },
  loadingColumnFree: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    flex: 1,
    minWidth: 0,
  },
  adColumn: {
    display: {xs: 'none', sm: 'flex'},
    flexShrink: 0,
  },
  progressBar: {
    width: '100%',
    maxWidth: 320,
    borderRadius: 4,
    height: 6,
    // Disable MUI's .4s value transition. We push `value` from a 60 Hz RAF loop
    // (free mode) and from per-frame increments (paid mode); on iOS WebKit the
    // running transition collides with each new target and shows as a flicker.
    '& .MuiLinearProgress-bar': {
      transition: 'none',
    },
  },
  loadingText: {
    fontSize: 12,
    color: tokens.text.muted,
  },
  sceneCard: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '8px',
    border: `1px solid ${tokens.accordion.border}`,
    borderRadius: '8px',
    padding: '12px',
  },
  sceneHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
  },
  sceneLabel: {
    fontSize: 13,
    fontWeight: 600,
    color: tokens.text.primary,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  dimensionLabel: {
    fontSize: 11,
    color: tokens.text.muted,
    fontWeight: 500,
  },
  sceneDownloadButton: {
    fontSize: 11,
    textTransform: 'none' as const,
    alignSelf: 'flex-end',
  },
  carouselGroup: {
    gridColumn: '1 / -1',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '10px',
    padding: '12px',
    borderRadius: '10px',
    border: `1px solid ${tokens.accordion.border}`,
    background: 'rgba(255, 255, 255, 0.02)',
  },
  carouselHeaderInfo: {
    display: 'flex',
    flexDirection: 'column' as const,
    minWidth: 0,
    flex: 1,
    gap: '2px',
  },
  carouselFrames: {
    display: 'grid',
    // gridTemplateColumns is overridden per-group inline so all N frames sit
    // side-by-side in one row, shrinking to fit when N is large.
    gap: '10px',
  },
  frameCard: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '6px',
    border: `1px solid ${tokens.accordion.border}`,
    borderRadius: '6px',
    padding: '8px',
    backgroundColor: tokens.sidebar.background,
  },
  frameLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: tokens.text.secondary,
  },
  groupDownloadButton: {
    fontSize: 11,
    textTransform: 'none' as const,
    flexShrink: 0,
  },
  actions: {
    padding: '12px 24px',
    gap: '8px',
  },
  closeButton: {
    fontSize: 12,
    textTransform: 'none' as const,
    color: tokens.text.secondary,
  },
  downloadButton: {
    fontSize: 12,
    textTransform: 'none' as const,
  },
})

export default ExportDialog
